import { ZOOM_STEP } from "../../../../constants"

export const PAN_STEP = 56
export const MIN_SCALE = 1
export const MAX_SCALE = 2

export type PanDirection = "up" | "down" | "left" | "right"

export type TransformPosition = {
  positionX: number
  positionY: number
}

export type TransformState = TransformPosition & {
  scale: number
}

export type TransformBounds = {
  minPositionX: number
  maxPositionX: number
  minPositionY: number
  maxPositionY: number
}

export type TransformBoundsInput = {
  wrapperWidth: number
  wrapperHeight: number
  contentWidth: number
  contentHeight: number
  scale: number
  centerZoomedOut?: boolean
}

export const roundPosition = (value: number) => Math.round(value * 100) / 100

export const clampScale = (scale: number) =>
  Math.max(MIN_SCALE, Math.min(MAX_SCALE, roundPosition(scale)))

export const getNextScale = (scale: number, decrease = false) =>
  clampScale(
    Math.round((scale + (decrease ? -ZOOM_STEP : ZOOM_STEP)) * 10) / 10
  )

export const getPannedPosition = (
  positionX: number,
  positionY: number,
  direction: PanDirection
): TransformPosition => {
  switch (direction) {
    case "left":
      return { positionX: positionX + PAN_STEP, positionY }
    case "right":
      return { positionX: positionX - PAN_STEP, positionY }
    case "up":
      return { positionX, positionY: positionY + PAN_STEP }
    case "down":
      return { positionX, positionY: positionY - PAN_STEP }
  }
}

export const clampPosition = (value: number, min: number, max: number) =>
  roundPosition(Math.min(Math.max(value, min), max))

export const getBoundedPosition = (
  position: TransformPosition,
  bounds?: TransformBounds | null
): TransformPosition => {
  if (!bounds) return position

  return {
    positionX: clampPosition(
      position.positionX,
      bounds.minPositionX,
      bounds.maxPositionX
    ),
    positionY: clampPosition(
      position.positionY,
      bounds.minPositionY,
      bounds.maxPositionY
    )
  }
}

export const getCenteredZoomPosition = (
  transformState: TransformState,
  wrapperSize: { width: number; height: number },
  newScale: number
): TransformPosition => {
  if (transformState.scale === 0) {
    return {
      positionX: transformState.positionX,
      positionY: transformState.positionY
    }
  }

  const scaleDifference = newScale - transformState.scale
  const centerX = wrapperSize.width / 2
  const centerY = wrapperSize.height / 2
  const focalPointX =
    (centerX - transformState.positionX) / transformState.scale
  const focalPointY =
    (centerY - transformState.positionY) / transformState.scale

  return {
    positionX: roundPosition(
      transformState.positionX - focalPointX * scaleDifference
    ),
    positionY: roundPosition(
      transformState.positionY - focalPointY * scaleDifference
    )
  }
}

export const getTransformBounds = ({
  wrapperWidth,
  wrapperHeight,
  contentWidth,
  contentHeight,
  scale,
  centerZoomedOut = false
}: TransformBoundsInput): TransformBounds => {
  const scaledContentWidth = contentWidth * scale
  const scaledContentHeight = contentHeight * scale
  const diffWidth = wrapperWidth - scaledContentWidth
  const diffHeight = wrapperHeight - scaledContentHeight
  const scaleWidthFactor =
    wrapperWidth > scaledContentWidth
      ? diffWidth * (centerZoomedOut ? 1 : 0.5)
      : 0
  const scaleHeightFactor =
    wrapperHeight > scaledContentHeight
      ? diffHeight * (centerZoomedOut ? 1 : 0.5)
      : 0

  return {
    minPositionX: roundPosition(
      wrapperWidth - scaledContentWidth - scaleWidthFactor
    ),
    maxPositionX: roundPosition(scaleWidthFactor),
    minPositionY: roundPosition(
      wrapperHeight - scaledContentHeight - scaleHeightFactor
    ),
    maxPositionY: roundPosition(scaleHeightFactor)
  }
}
